import type { CatalogSchedule } from "@school-sales-ai/shared";
import { EditableRowList } from "./EditableRowList";
import { PriceInput } from "./PriceInput";
import {
  commitHandlers,
  CYCLES_SCHEDULE,
  cyclesTypeLabel,
  fieldInvalid,
  rowErrorMessages,
  UNDATED_SCHEDULE,
} from "./row-fields";
import { RowSaveFooter, RowStatusIcon } from "./RowSaveState";
import { fieldClass } from "./fields";
import type { CatalogEditor, EditMode, EditorRow } from "./useCatalogEditor";

type ProductRow = EditorRow<"products">;

export type ProductGroupEditorProps = {
  editor: CatalogEditor;
  /** The group's rows: the catalog's root, or one category. */
  rows: ProductRow[];
  /** The group new rows are added to; null is the root. */
  categoryId: string | null;
  title: string;
  /**
   * The category's saved name, when this group is one. A school's placeholder
   * uses it to suggest a FULL name («1° de primaria»): product names are unique
   * across the whole catalog, and travel without their category everywhere else.
   */
  categoryName?: string;
  /** Replaces the title row — a category's editable name and menu. */
  header?: React.ReactNode;
  /**
   * The school shape: each row shows its calendar, and adding asks whether the
   * program runs by cycle or on its own dates. Off: one flat row with a price.
   */
  enrollmentEnabled: boolean;
  /**
   * A tenant that closes by quote (plan `quotes`): no price column. The team
   * prices outside the system and the agent never sees one. Hidden, never
   * cleared — a stored price is left as it is.
   */
  pricesHidden?: boolean;
  /**
   * The cycles switch. It only renames the first choice of «Agregar programa»:
   * «Por ciclo» with cycles, «Inscripción abierta» without — an academy still
   * offers a dated course now and then, so «Con fechas» is always there.
   */
  usesCycles: boolean;
  /** Why rows cannot be added yet (a category whose name is not saved). */
  addDisabledReason?: string;
  onRemove: (row: ProductRow) => void;
  /** After ▲/▼, with this group's rows in their new order. */
  onReorder: (rows: ProductRow[]) => void;
  /** The media and description indicators, on the catalog page. */
  renderProductExtra?: (row: ProductRow) => React.ReactNode;
  /** The row's "⋯" in place of its X. */
  renderRowMenu?: (row: ProductRow) => React.ReactNode;
  /** Answers a specific save error with an action of its own («Reactivar»). */
  renderErrorAction?: (row: ProductRow) => React.ReactNode;
  emptyLabel: string;
  /** Set on desktop, inside the form's `DndContext`: rows get a drag handle. */
  dropGroupId?: string;
};

/**
 * One group of products — the root or a category — and the only product
 * editor. It replaced the school's two fixed sections by calendar
 * («Programas por ciclo» / «con fechas propias»): categories now say what a
 * program IS, and its calendar is a property of the row, so one group can hold
 * a licenciatura by cycle next to a diplomado with its own dates.
 */
export function ProductGroupEditor({
  editor,
  rows,
  categoryId,
  title,
  categoryName,
  header,
  enrollmentEnabled,
  pricesHidden = false,
  usesCycles,
  addDisabledReason,
  onRemove,
  onReorder,
  renderProductExtra,
  renderRowMenu,
  renderErrorAction,
  emptyLabel,
  dropGroupId,
}: ProductGroupEditorProps): React.JSX.Element {
  const noun = enrollmentEnabled ? "programa" : "producto";

  const add = (schedule: CatalogSchedule | null): void =>
    editor.add("products", {
      name: "",
      description: null,
      attributes: [],
      schedule,
      priceCents: null,
      categoryId,
    });

  return (
    <EditableRowList<ProductRow>
      title={title}
      header={header}
      items={rows}
      getKey={(row) => row.key}
      onAdd={() => add(null)}
      addOptions={
        enrollmentEnabled
          ? [
              { label: cyclesTypeLabel(usesCycles), onSelect: () => add(CYCLES_SCHEDULE) },
              { label: "Con fechas", onSelect: () => add(UNDATED_SCHEDULE) },
            ]
          : undefined
      }
      addDisabledReason={addDisabledReason}
      dropGroupId={dropGroupId}
      onRemove={onRemove}
      onReorder={onReorder}
      canMove={(row) => row.id !== null}
      addLabel={`Agregar ${noun}`}
      emptyLabel={emptyLabel}
      rowName={(row, i) =>
        row.current.name.trim() ? `el ${noun} ${row.current.name}` : `el ${noun} ${i + 1}`
      }
      rowErrors={(row) => rowErrorMessages(editor, "products", row.key)}
      renderRowStatus={(row) => <RowStatusIcon status={row.status} />}
      renderRowFooter={(row) => (
        <RowSaveFooter
          editor={editor}
          list="products"
          row={row}
          renderErrorAction={renderErrorAction}
        />
      )}
      renderRowMenu={renderRowMenu}
      renderRow={(row, index) => (
        <>
          <input
            className={`${fieldClass({
              invalid: fieldInvalid(editor, "products", row.key, "name"),
            })} min-w-[160px] flex-1`}
            value={row.current.name}
            placeholder={
              !enrollmentEnabled
                ? "Tequila Reposado 750ml"
                : categoryName
                  ? `p. ej. 1° de ${categoryName.toLocaleLowerCase("es")}`
                  : "Diseño Gráfico"
            }
            aria-label={`Nombre del ${noun} ${index + 1} de ${title}`}
            onChange={(e) =>
              editor.edit("products", row.key, { name: e.target.value }, "debounce")
            }
            {...commitHandlers(editor, "products", row.key)}
          />
          {enrollmentEnabled ? (
            // The school shape sells nothing through its catalog: no price.
            // A stored price is never touched — nothing here sends it.
            row.current.schedule?.type === "specific_dates" && (
              <DateRange editor={editor} row={row} label={`${noun} ${index + 1} de ${title}`} />
            )
          ) : pricesHidden ? null : (
            <PriceInput
              valueCents={row.current.priceCents}
              currency={editor.settings.currency}
              onChange={(priceCents) =>
                editor.edit("products", row.key, { priceCents }, "debounce")
              }
              onCommit={() => void editor.commit("products", row.key)}
              invalid={fieldInvalid(editor, "products", row.key, "priceCents")}
              ariaLabel={`Precio del ${noun} ${index + 1} de ${title}`}
            />
          )}
          {renderProductExtra?.(row)}
        </>
      )}
    />
  );
}

/**
 * A program's own dates, or «Sin fecha definida».
 *
 * The block is ONE unit — both dates and the checkbox — so a narrow screen moves
 * it under the name whole instead of peeling the end date off onto a line of its
 * own. Dates save when the range is whole: a start date alone is a half-open
 * range the schema refuses, so the field waits for the debounce or the blur
 * rather than failing on the first pick.
 */
function DateRange({
  editor,
  row,
  label,
}: {
  editor: CatalogEditor;
  row: ProductRow;
  label: string;
}): React.JSX.Element {
  const schedule = row.current.schedule;
  // Both dates null is the "no confirmed schedule" case. The checkbox owns it,
  // so a half-open range — which the schema rejects — cannot be expressed.
  const undated = schedule?.startDate === null && schedule?.endDate === null;
  const invalid = fieldInvalid(editor, "products", row.key, "schedule");

  const setDates = (
    patch: Partial<Pick<CatalogSchedule, "startDate" | "endDate">>,
    mode: EditMode,
  ): void => {
    if (!schedule) return;
    editor.edit("products", row.key, { schedule: { ...schedule, ...patch } }, mode);
  };

  return (
    <div className="flex min-w-[292px] flex-wrap items-center gap-2 sm:min-w-[450px]">
      <div className="flex shrink-0 gap-2">
        <input
          type="date"
          className={`${fieldClass({ mono: true, invalid })} w-[142px] shrink-0`}
          value={schedule?.startDate ?? ""}
          disabled={undated}
          aria-label={`Fecha de inicio del ${label}`}
          onChange={(e) => setDates({ startDate: e.target.value || null }, "debounce")}
          onBlur={() => void editor.commit("products", row.key)}
        />
        <input
          type="date"
          className={`${fieldClass({ mono: true, invalid })} w-[142px] shrink-0`}
          value={schedule?.endDate ?? ""}
          disabled={undated}
          aria-label={`Fecha de fin del ${label}`}
          onChange={(e) => setDates({ endDate: e.target.value || null }, "debounce")}
          onBlur={() => void editor.commit("products", row.key)}
        />
      </div>
      <label className="flex shrink-0 items-center gap-1.5 text-[12.5px] text-ink-500">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-moss"
          checked={undated}
          aria-label={`Sin fecha definida para el ${label}`}
          onChange={(e) =>
            e.target.checked
              ? // Undated is a complete, valid schedule: save it now.
                setDates({ startDate: null, endDate: null }, "now")
              : // Unchecking opens two empty dates to fill; nothing to save
                // until they are.
                setDates({ startDate: "", endDate: "" }, "debounce")
          }
        />
        Sin fecha definida
      </label>
    </div>
  );
}
